import { z } from 'zod';
import { isoDateTime } from './common';

/**
 * One stat item displayed on the About page's "chiffres" row.
 * `value` is the big number / chip (free-text so admins can use "2025",
 * "6", "+12", "8–14", etc.); `label` is the line under it.
 */
export const siteStatSchema = z.object({
  value: z.string().min(1).max(50),
  label: z.string().min(1).max(120),
});
export type SiteStat = z.infer<typeof siteStatSchema>;

export const siteContentSchema = z.object({
  hero: z.object({
    eyebrow: z.string(),
    title: z.string(),
    subtitle: z.string(),
    primaryCtaLabel: z.string(),
    primaryCtaHref: z.string(),
  }),
  about: z.object({
    title: z.string(),
    body: z.string(),
    /** Lede shown under the About hero — what visitors see right away. */
    lede: z.string().default(''),
  }),
  /** Stats row on the About page. Admin-editable; up to ~6 items renders well. */
  stats: z.array(siteStatSchema).default([]),
  contact: z.object({
    email: z.string().email(),
    phone: z.string(),
    address: z.string(),
    /** Admins listed here receive transactional notifications (new inscriptions, etc.). */
    notificationEmails: z.array(z.string().email()).default([]),
  }),
  footer: z.object({
    tagline: z.string(),
    legal: z.string(),
  }),
  /**
   * Public social-media URLs for the centre. Each field is optional and
   * stored as a loose string (handles like `@moroccantraining.center` are
   * accepted) — the footer component normalises to a full URL at render time.
   */
  socials: z
    .object({
      instagram: z.string().max(300).optional(),
      facebook: z.string().max(300).optional(),
      tiktok: z.string().max(300).optional(),
    })
    .default({}),
  updatedAt: isoDateTime,
});
export type SiteContent = z.infer<typeof siteContentSchema>;
